import { useMutation, type UseMutateAsyncFunction, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/query-keys';
import { apiFetch } from '@/lib/api/client';

interface DeclinePayload {
  comments: string;
}

async function declineTrip({
  tripId,
  comment,
}: {
  tripId: string;
  comment: string;
}): Promise<void> {
  const response = await apiFetch(`/api/v1/business-trip-requests/${tripId}/decline`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ comments: comment } satisfies DeclinePayload),
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const message =
      payload && typeof payload === 'object' && 'error' in (payload as Record<string, unknown>)
        ? String(
            (payload as Record<string, { message?: string }>).error?.message ??
              'Failed to decline trip'
          )
        : 'Failed to decline trip';

    throw new Error(message);
  }
}

interface DeclineVariables {
  tripId: string;
  comment: string;
}

interface DeclineMutation {
  mutate: UseMutateAsyncFunction<void, Error, DeclineVariables, unknown>;
  isPending: boolean;
}

export function useDeclineTrip(): DeclineMutation {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: declineTrip,
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trips });
      queryClient.invalidateQueries({ queryKey: queryKeys.tripApprovals });
      if (variables?.tripId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.trip(variables.tripId) });
      }
    },
    onError: (error) => {
      console.error('Error declining trip:', error);
    },
  });

  return {
    mutate: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}
